import * as THREE from 'three/webgpu';
import type {Viewer} from '../../viewer/Viewer';
import {ViewerMode} from '../../viewer/ViewerMode';
import {
  type FeatureCurve,
  type FeatureFace,
  type FeatureLine,
  type FeaturePoint,
  type FeatureSurface,
  isFeatureCurve,
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
  isFeatureSurface,
} from '../../mesh/feature';
import {buildCircleFromThreePoints2D} from '../../tool/buildCircleFromThreePoints2D';
import {type DatumAxis, isDatumAxis} from '../DatumAxis';
import {
  buildDatumReferenceEntityId,
  DatumReferenceEntityKind,
  parseDatumReferenceEntityId
} from '../DatumReferenceEntityId';
import {getDatumPlaneIdFromObject} from '../DatumPlane';
import {type DatumPoint, isDatumPoint} from '../DatumPoint';
import {
  DATUM_AXIS_HISTORY_ENTITY_ID,
  type DatumAxisFeatureHistoryRecord,
  type DatumAxisHistorySnapshot,
  type DatumAxisHistoryVector3,
  type DatumAxisResolvedGeometryHistoryRecord,
} from '../DatumAxisHistoryTypes';
import {ReferenceCreationContext} from '../ReferenceCreationContext';
import {
  AxisMode,
  AxisSurfaceType,
  ReferenceCommandLifecycle,
  ReferenceCommandModeSource,
  ReferenceEntitySemanticType,
} from '../ReferenceFeatureTypes';
import {
  resolveImportedAssemblyTopologySelectionRef
} from '../../imported-model/selection/resolveImportedAssemblyTopologySelectionRef';
import {resolveTopologySelectionPick} from '../../utils/feature/resolveTopologySelectionPick';
import {resolveModelTopologyProxyObject} from '../../utils/feature/resolveModelTopologyOwnerResultMesh';
import {resolveAssemblyTopologyReferenceObject} from '../shared/resolveAssemblyTopologyReferenceObject';
import {
  type AxisEntityReference,
  type AxisFeatureDefinition,
  type AxisResolvedEntity,
  type AxisResolvedGeometry,
  type AxisSolveError,
  type AxisTolerancePolicy,
  DEFAULT_AXIS_TOLERANCE,
  ReferenceAxisSolver,
} from './ReferenceAxisSolver';
import {resolveFeatureSurfaceAxis} from './resolveFeatureSurfaceAxis';
import {readFeatureCurveWorldSamplePoints, resolveCircularSolvePoints} from '../tool/ReferenceCircularEdgeUtils';

/**
 * 基准轴选择实体分类枚举。
 */
export enum AxisSelectionKind {
  /** 线性实体。 */
  Linear = 'linear',
  /** 平面类实体。 */
  PlaneLike = 'planeLike',
  /** 点类实体。 */
  PointLike = 'pointLike',
  /** 曲面对象。 */
  CylOrConeFace = 'cylOrConeFace',
  /** 既有基准轴。 */
  ReferenceAxis = 'referenceAxis',
  /** 临时轴。 */
  TemporaryAxis = 'temporaryAxis',
  /** 不支持实体。 */
  Unsupported = 'unsupported',
}

/**
 * 基准轴选择候选。
 */
export interface AxisSelectionCandidate {
  /** 实体 ID。 */
  entityId: string;
  /** 实体类型。 */
  entityType: ReferenceEntitySemanticType;
  /** 展示标签。 */
  label: string;
  /** 实体分类。 */
  kind: AxisSelectionKind;
  /** 扩展载荷。 */
  payload?: Record<string, unknown>;
}

/**
 * 基准轴选择规则。
 */
export interface AxisSelectionRule {
  /** 对应模式。 */
  mode: AxisMode;
  /** 最小选择数量。 */
  minCount: number;
  /** 最大选择数量。 */
  maxCount: number;
  /** 候选接收谓词。 */
  accept: (candidate: AxisSelectionCandidate) => boolean;
}

/**
 * 基准轴预览状态。
 */
export interface AxisPreviewState {
  /** 是否求解成功。 */
  ok: boolean;
  /** 预览几何。 */
  geometry?: AxisResolvedGeometry;
  /** 失败信息。 */
  error?: AxisSolveError;
}

/**
 * 基准轴特征记录。
 */
export interface AxisFeatureRecord {
  /** 特征 ID。 */
  featureId: string;
  /** 正式对象创建来源上下文。 */
  creationContext: ReferenceCreationContext;
  /** 特征定义。 */
  definition: AxisFeatureDefinition;
  /** 已解析几何。 */
  resolvedGeometry?: AxisResolvedGeometry;

    /**
   * 线/边/轴模式来源线段（用于渲染同长度显示）。
   */
  sourceLinearSegment?: {
    /** 线段起点。 */
    start: THREE.Vector3;
    /** 线段终点。 */
    end: THREE.Vector3;
  };

    /** 是否隐藏。 */
  hidden: boolean;
  /** 正式基准轴 ID。 */
  datumAxisId?: number;
  /** 是否压缩。 */
  suppressed?: boolean;
  /** 错误信息。 */
  error?: string;
}

/**
 * 基准轴命令状态。
 */
export interface AxisCommandState {
  /** 生命周期。 */
  lifecycle: ReferenceCommandLifecycle;
  /** 当前模式。 */
  mode: AxisMode;
  /** 模式来源。 */
  modeSource: ReferenceCommandModeSource;
  /** 当前参考集合。 */
  references: AxisSelectionCandidate[];
  /** 当前预览状态。 */
  preview: AxisPreviewState;
  /** 是否已修改。 */
  dirty: boolean;
}

/**
 * 基准轴控制器配置。
 */
export interface AxisCommandControllerOptions {
  /** 容差覆盖。 */
  tolerance?: Partial<AxisTolerancePolicy>;
  /** 取消回调。 */
  onCancelled?: () => void;
}

/**
 * 基准轴控制器视图模型。
 */
export interface AxisCommandViewModel {
  /** 当前状态。 */
  state: AxisCommandState;
  /** 是否可提交。 */
  canSubmit: boolean;
  /** 特征记录。 */
  featureRecords: AxisFeatureRecord[];
  /** 选择摘要。 */
  selectionSummary: Array<{ entityId: string; label: string }>;
}

/**
 * 格式化基准轴调试向量。
 *
 * @param value 向量值。
 * @returns 便于控制台查看的普通对象。
 */
function formatReferenceAxisDebugVector(value: THREE.Vector3 | undefined | null): { x: number; y: number; z: number } | null {
  if (!value) {
    return null;
  }
  return {
    x: value.x,
    y: value.y,
    z: value.z,
  };
}

/**
 * 计算显示线段长度。
 *
 * @param geometry 已解析几何。
 * @returns 线段长度。
 */
function resolveReferenceAxisDebugLength(geometry: AxisResolvedGeometry | undefined): number | null {
  if (!geometry) {
    return null;
  }
  return geometry.displaySegment.start.distanceTo(geometry.displaySegment.end);
}

/**
 * 输出基准轴创建/重算调试信息。
 *
 * @param message 调试消息。
 * @param payload 调试载荷。
 */
function logReferenceAxisControllerDebug(message: string, payload: Record<string, unknown>): void {
  console.log(`[基准轴创建重算调试] ${message}`, payload);
}

/**
 * 读取对象世界包围盒。
 *
 * 说明：
 * 1. 双面建轴的显示长度优先使用面外环与交线的重叠区间；
 * 2. 当拖动后外环顶点暂时不可用时，仍需要包围盒作为长度兜底；
 * 3. 这里统一对当前对象调用 `setFromObject`，避免上层重复拼装世界坐标范围。
 *
 * @param object 场景对象。
 * @returns 世界包围盒；为空时返回 `null`。
 */
function resolveReferenceAxisWorldBounds(
  object: THREE.Object3D,
): { min: THREE.Vector3; max: THREE.Vector3 } | null {
  const bounds = new THREE.Box3().setFromObject(object);
  if (bounds.isEmpty()) {
    return null;
  }
  return {
    min: bounds.min.clone(),
    max: bounds.max.clone(),
  };
}

/**
 * 轴命令打开迁移结果。
 */
export interface AxisOpenTransitionResult {
  /** 目标模式。 */
  mode: AxisMode;
  /** 归一化参考。 */
  references: AxisSelectionCandidate[];
}


/**
 * 基准轴编辑特征会话。
 */
interface ReferenceAxisEditSession {
  /** 正在编辑的特征 ID。 */
  featureId: string;
  /** 正在编辑的正式基准轴 ID。 */
  datumAxisId: number;
  /** 编辑前正式基准轴名称。 */
  axisName: string;
  /** 编辑前正式基准轴可见性。 */
  previousVisible: boolean;
}


/**
 * 轴命令选择迁移结果。
 */
export interface AxisSelectionTransitionResult {
  /** 目标模式。 */
  mode: AxisMode;
  /** 归一化参考。 */
  references: AxisSelectionCandidate[];
}

/**
 * 基准轴可接收的选择对象。
 */
type AxisSelectableObject = FeatureFace | FeatureLine | FeaturePoint | DatumAxis | DatumPoint | THREE.Object3D;

/**
 * 基准轴选择拓扑载荷。
 */
interface AxisSelectionTopologyPayload extends Record<string, unknown> {
  /** 装配体导入拓扑稳定引用。 */
  assemblyTopologyRef?: AssemblyTopologySelectionRef | null;

    /**
   * 选择层解析出的拓扑命中快照。
   * 特征 replay 后旧 uuid 失效，重算链路通过此字段从新结果体上解析代理对象。
   */
  topologyPick?: ResolvedTopologyPick | null;

}


/**
 * 构建装配体拓扑选择载荷。
 *
 * @param object 当前选择对象。
 * @returns 拓扑选择载荷。
 */
function buildAxisSelectionTopologyPayload(object: THREE.Object3D): AxisSelectionTopologyPayload {
  const topologyPick = resolveTopologySelectionPick(object);
  return {
    assemblyTopologyRef: resolveImportedAssemblyTopologySelectionRef(object, topologyPick),

      // 同步保存拓扑命中快照，供特征重建后重算链路按索引从新结果体取回代理对象。
    topologyPick,

  };
}

/**
 * 从候选载荷读取装配体拓扑稳定引用。
 *
 * @param payload 候选载荷。
 * @returns 稳定拓扑引用；不存在时返回 `null`。
 */
function readAxisSelectionAssemblyTopologyRef(
  payload: Record<string, unknown> | undefined,
): AssemblyTopologySelectionRef | null {
  const assemblyTopologyRef = (payload as AxisSelectionTopologyPayload | undefined)?.assemblyTopologyRef;
  return assemblyTopologyRef ?? null;
}


/**
 * 从候选载荷读取拓扑命中快照。
 *
 * @param payload 候选载荷。
 * @returns 拓扑命中快照；不存在时返回 `null`。
 */
function readAxisSelectionTopologyPick(
  payload: Record<string, unknown> | undefined,
): ResolvedTopologyPick | null {
  const topologyPick = (payload as AxisSelectionTopologyPayload | undefined)?.topologyPick;
  return topologyPick ?? null;
}


/**
 * 判断基准轴定义是否依赖装配体导入拓扑。
 *
 * @param definition 基准轴特征定义。
 * @returns 若任意引用保存了装配体拓扑稳定引用则返回 `true`。
 */
function hasAssemblyTopologyAxisReference(definition: AxisFeatureDefinition): boolean {
  if (!definition || typeof definition !== 'object') {
    return false;
  }
  if (!Array.isArray(definition.references)) {
    return false;
  }
  return definition.references.some((reference) => Boolean(reference.assemblyTopologyRef));
}


/**
 * 判断曲面对象是否可按轴语义解析处理。
 *
 * @param object 场景对象。
 * @returns 若可解析出轴语义则返回解析结果，否则返回 `null`。
 */
function resolveAxisSurfaceCandidate(object: THREE.Object3D): ReturnType<typeof resolveFeatureSurfaceAxis> {
  if (!isFeatureSurface(object)) {
    return null;
  }
  return resolveFeatureSurfaceAxis(object as FeatureSurface);
}

/**
 * 将曲面轴解析类型映射为拓扑曲面类型。
 *
 * @param surfaceType 曲面轴解析类型。
 * @returns 拓扑曲面类型。
 */
function mapAxisSurfaceTypeToTopologySurfaceKind(surfaceType: AxisSurfaceType): TopologySurfaceKind {
  return surfaceType === AxisSurfaceType.Cylindrical ? 'cylinder' : 'cone';
}

/**
 * 创建基准轴特征 ID。
 *
 * @returns 基准轴特征唯一标识。
 */
function createAxisFeatureId(): string {
  return `datum-axis:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 创建默认命令状态。
 *
 * @returns 默认状态。
 */
function createDefaultState(): AxisCommandState {
  return {
    lifecycle: ReferenceCommandLifecycle.Closed,
    mode: AxisMode.LineEdgeAxis,
    modeSource: ReferenceCommandModeSource.AutoInferred,
    references: [],
    preview: { ok: false },
    dirty: false,
  };
}

/**
 * 根据 Viewer 模式解析正式参考对象创建来源。
 *
 * @param mode 当前 Viewer 模式。
 * @returns 创建来源上下文。
 */
function resolveReferenceCreationContextFromViewerMode(mode: ViewerMode): ReferenceCreationContext {
  return (mode === ViewerMode.Assembly || mode === ViewerMode.MoveComponent)
    ? ReferenceCreationContext.Assembly
    : ReferenceCreationContext.PartFeature;
}


/**
 * 根据正式参考对象创建来源解析命令结束后的宿主模式。
 *
 * @param context 正式对象创建来源。
 * @returns 命令结束后应恢复的 Viewer 模式。
 */
function resolveReferenceCommandExitViewerMode(context: ReferenceCreationContext): ViewerMode {
  return context === ReferenceCreationContext.Assembly
    ? ViewerMode.Assembly
    : ViewerMode.Default;
}



/**
 * 将未知值解析为有限三维向量。
 *
 * @param value 待解析值。
 * @returns 解析成功时返回三维向量，否则返回 `null`。
 */
function toFiniteVector3(value: unknown): THREE.Vector3 | null {
  if (!(value instanceof THREE.Vector3)) {
    return null;
  }
  if (!Number.isFinite(value.x) || !Number.isFinite(value.y) || !Number.isFinite(value.z)) {
    return null;
  }
  // 基准轴缓存只接受当前几何链路产出的 Vector3，禁止从三元数组隐式恢复世界坐标。
  return value.clone();
}

/**
 * 从选择候选中提取线段。
 *
 * @param selection 选择候选。
 * @returns 若存在可用线段返回起终点，否则返回 null。
 */
function tryExtractLinearSegmentFromSelection(selection: AxisSelectionCandidate | null): {
  start: THREE.Vector3;
  end: THREE.Vector3;
} | null {
  if (!selection || !selection.payload) {
    return null;
  }
  const payload = selection.payload as Record<string, unknown>;
  const startPoint = payload.startPoint;
  const endPoint = payload.endPoint;
  const start = toFiniteVector3(startPoint);
  const end = toFiniteVector3(endPoint);
  if (start && end) {
    return {
      start,
      end,
    };
    }
  return null;
}



/**
 * 归一化向量。
 *
 * @param value 待归一化向量。
 * @returns 单位向量；退化时返回 `null`。
 */
function normalizeVec3(value: THREE.Vector3): THREE.Vector3 | null {
  const length = value.length();
  if (!Number.isFinite(length) || length <= 1e-12) {
    return null;
  }
  return value.clone().multiplyScalar(1 / length);
}

/**
 * 圆边解析结果。
 */
interface CircularEdgeGeometryPayload {
  /** 圆心。 */
  center: THREE.Vector3;
  /** 法向。 */
  normal: THREE.Vector3;
}

/**
 * 解析圆形特征曲线的几何参数。
 *
 * @param curve 特征曲线对象。
 * @returns 圆边几何参数；解析失败时返回 `null`。
 */
function resolveCircularEdgeGeometryPayload(curve: FeatureCurve): CircularEdgeGeometryPayload | null {
  const sampledPoints = readFeatureCurveWorldSamplePoints(curve);
  const solvePoints = resolveCircularSolvePoints(sampledPoints);
  if (!solvePoints) {
    return null;
  }

  const [firstPoint, middlePoint, lastPoint] = solvePoints;
  const localXAxis = normalizeVec3(middlePoint.clone().sub(firstPoint));
  const planeNormal = normalizeVec3(
    middlePoint.clone().sub(firstPoint).cross(lastPoint.clone().sub(firstPoint)),
  );
  if (!localXAxis || !planeNormal) {
    return null;
  }

  const localYAxis = normalizeVec3(planeNormal.clone().cross(localXAxis));
  if (!localYAxis) {
    return null;
  }

  const middleOffset = middlePoint.clone().sub(firstPoint);
  const lastOffset = lastPoint.clone().sub(firstPoint);
  const circleDefinition = buildCircleFromThreePoints2D(
    new THREE.Vector2(0, 0),
    new THREE.Vector2(
      middleOffset.dot(localXAxis),
      middleOffset.dot(localYAxis),
    ),
    new THREE.Vector2(
      lastOffset.dot(localXAxis),
      lastOffset.dot(localYAxis),
    ),
  );
  if (!circleDefinition) {
    return null;
  }

  const center = firstPoint.clone()
    .addScaledVector(localXAxis, circleDefinition.center.x)
    .addScaledVector(localYAxis, circleDefinition.center.y);
  return {
    center,
    normal: planeNormal,
  };
}

/**
 * 圆柱/圆锥面轴线解析结果。
 */
interface CylOrConeAxisResolution {
  /** 轴线原点。 */
  origin: THREE.Vector3;
  /** 轴线方向。 */
  direction: THREE.Vector3;
  /** 轴线显示长度。 */
  displayLength?: number;
}

/**
 * 从圆柱/圆锥面代理中提取轴线信息。
 *
 * 设计说明：
 * 1. 优先使用相邻圆边的圆心连线作为轴线方向；
 * 2. 若只有一个圆边（例如局部截面），退回圆边法向；
 * 3. 无法解析时返回 `null`，由上层给出诊断错误。
 *
 * @param face 面代理对象。
 * @returns 轴线解析结果；失败时返回 `null`。
 */
function resolveCylOrConeAxisFromFeatureFace(face: FeatureFace): CylOrConeAxisResolution | null {
  const circularPayloads = face
    .getBoundaryLines()
    .filter((line): line is FeatureCurve => isFeatureCurve(line) && line.curveKind === 'circle')
    .map((curve) => resolveCircularEdgeGeometryPayload(curve))
    .filter((payload): payload is CircularEdgeGeometryPayload => payload !== null);

  if (circularPayloads.length <= 0) {
    return null;
  }

  if (circularPayloads.length >= 2) {
    let bestPair: [CircularEdgeGeometryPayload, CircularEdgeGeometryPayload] | null = null;
    let bestDistanceSq = -1;
    for (let i = 0; i < circularPayloads.length; i += 1) {
      for (let j = i + 1; j < circularPayloads.length; j += 1) {
        const distanceSq = circularPayloads[i]!.center.distanceToSquared(circularPayloads[j]!.center);
        if (distanceSq > bestDistanceSq) {
          bestDistanceSq = distanceSq;
          bestPair = [circularPayloads[i]!, circularPayloads[j]!];
        }
      }
    }

    if (bestPair) {
      const centerDelta = bestPair[1].center.clone().sub(bestPair[0].center);
      const axisDirection = normalizeVec3(centerDelta);
      if (axisDirection) {
        return {
          origin: bestPair[0].center.clone().add(bestPair[1].center).multiplyScalar(0.5),
          direction: axisDirection,
          displayLength: centerDelta.length(),
        };
      }
    }
  }

  const fallbackDirection = normalizeVec3(circularPayloads[0]!.normal);
  if (!fallbackDirection) {
    return null;
  }
  return {
    origin: circularPayloads[0]!.center.clone(),
    direction: fallbackDirection,
  };
}


/**
 * 基准轴命令控制器。
 *
 * 设计说明：
 * 1. 将原 `commands` 目录中分散的选择规则、模式推断、迁移和动作统一内聚到本类；
 * 2. 对外只保留一个控制器文件，减少跨文件追踪成本；
 * 3. 类型、辅助函数与迁移逻辑全部围绕控制器收口，不改变既有对外行为。
 */
export class ReferenceAxisController {
  /** 选择规则集合。 */
  private static readonly AXIS_SELECTION_RULES: AxisSelectionRule[] = [
    {
      mode: AxisMode.LineEdgeAxis,
      minCount: 1,
      maxCount: 1,
      accept: (candidate) => (
        candidate.kind === AxisSelectionKind.Linear
        || candidate.kind === AxisSelectionKind.ReferenceAxis
        || candidate.kind === AxisSelectionKind.TemporaryAxis
      ),
    },
    {
      mode: AxisMode.TwoPlanes,
      minCount: 2,
      maxCount: 2,
      accept: (candidate) => candidate.kind === AxisSelectionKind.PlaneLike,
    },
    {
      mode: AxisMode.TwoPoints,
      minCount: 2,
      maxCount: 2,
      accept: (candidate) => candidate.kind === AxisSelectionKind.PointLike,
    },
    {
      mode: AxisMode.CylOrConeFace,
      minCount: 1,
      maxCount: 1,
      accept: (candidate) => candidate.kind === AxisSelectionKind.CylOrConeFace,
    },
    {
      mode: AxisMode.PointAndFaceOrPlane,
      minCount: 2,
      maxCount: 2,
      accept: (candidate) => (
        candidate.kind === AxisSelectionKind.PointLike
        || candidate.kind === AxisSelectionKind.PlaneLike
        || candidate.kind === AxisSelectionKind.CylOrConeFace
      ),
    },
  ];

  private readonly viewer: Viewer;
  private readonly options: AxisCommandControllerOptions;
  private readonly solver: ReferenceAxisSolver;
  private readonly tolerance: AxisTolerancePolicy;
  private state: AxisCommandState = createDefaultState();
  private records: AxisFeatureRecord[] = [];

    /** 当前基准轴编辑特征会话；为空表示创建模式。 */
  private editSession: ReferenceAxisEditSession | null = null;

    /** 当前命令会话的正式对象创建来源。 */
  private pendingCreationContext: ReferenceCreationContext = ReferenceCreationContext.PartFeature;
  /** 预览对象。 */
  private previewObject: THREE.Object3D | null = null;

  /**
   * 构造函数。
   *
   * @param viewer 视口渲染器。
   * @param options 控制器配置。
   */
  public constructor(viewer: Viewer, options: AxisCommandControllerOptions = {}) {
    this.viewer = viewer;
    this.options = options;
    this.solver = new ReferenceAxisSolver(this);
    this.tolerance = {
      ...DEFAULT_AXIS_TOLERANCE,
      ...(options.tolerance ?? {}),
    };
  }

  /**
   * 获取视图模型。
   *
   * @returns 当前视图模型。
   */
  public getViewModel(): AxisCommandViewModel {
    return {
      state: this.state,
      canSubmit: this.state.preview.ok,
      featureRecords: this.records,
      selectionSummary: this.state.references.map((item) => ({ entityId: item.entityId, label: item.label })),
    };
  }


    /**
   * 判断当前命令是否为编辑特征会话。
   *
   * @returns 编辑特征会话返回 `true`。
   */
  public isEditingFeature(): boolean {
    return this.editSession !== null;
  }

  /**
   * 判断指定正式基准轴是否支持编辑特征。
   *
   * @param axisId 正式基准轴 ID。
   * @returns 能匹配到可复算特征记录时返回 `true`。
   */
  public canEditFeatureByAxisId(axisId: number): boolean {
    return this.records.some((record) => record.datumAxisId === axisId);
  }

  /**
   * 规范化基准轴编辑提交前的历史快照。
   *
   * 设计说明：
   * 1. 编辑态会临时隐藏原正式基准轴，避免与预览轴重叠；
   * 2. 若 before 快照直接采集该临时状态，Undo 会回到隐藏态；
   * 3. 因此提交历史前需要把正在编辑目标的可见性回填为进入编辑前状态。
   *
   * @param snapshot 提交前采集的基准轴历史快照。
   * @returns 已归一化的历史快照。
   */
  public normalizeHistorySnapshotBeforeSubmit(snapshot: DatumAxisHistorySnapshot): DatumAxisHistorySnapshot {
    const activeEditSession = this.editSession;
    if (!activeEditSession) {
      return snapshot;
    }

    return {
      ...snapshot,
      axes: (snapshot.axes ?? []).map((axisRecord) => (
        axisRecord.axisId === activeEditSession.datumAxisId
          ? {
            ...axisRecord,
            visible: activeEditSession.previousVisible,
          }
          : axisRecord
      )),
    };
  }


    /**
   * 打开基准轴命令并应用预选结果。
   *
   * @param preselection 预选实体列表。
   */
  public open(preselection: AxisSelectionCandidate[] = []): void {
    this.restoreEditingAxisVisibilityIfNeeded();
    this.pendingCreationContext = resolveReferenceCreationContextFromViewerMode(this.viewer.getMode());
    this.editSession = null;
    const transition = this.resolveOpenTransition(preselection);
    this.state = {
      ...createDefaultState(),
      lifecycle: ReferenceCommandLifecycle.Open,
      mode: transition.mode,
      references: transition.references,
      dirty: false,
    };
    this.recomputePreview();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.setMode('reference-axis' as never);
    this.viewer.requestRender();
  }


    /**
   * 打开已有基准轴的编辑特征会话。
   *
   * 设计说明：
   * 1. 编辑特征必须恢复原定义参数，不允许从当前几何位置反推；
   * 2. 当前链路以 `AxisFeatureRecord` 作为参数真相源；
   * 3. 若记录缺失或引用失效，应拒绝进入编辑态，避免 UI 进入半恢复状态。
   *
   * @param axisId 待编辑的正式基准轴 ID。
   * @returns 打开结果。
   */
  public openEdit(axisId: number): { ok: boolean; reason?: string } {
    const targetAxis = this.viewer.getDatumAxis(axisId);
    if (!targetAxis) {
      return { ok: false, reason: '未找到目标基准轴，无法编辑' };
    }

    const targetRecord = this.records.find((record) => record.datumAxisId === axisId);
    if (!targetRecord) {
      return { ok: false, reason: '默认或导入基准轴不支持编辑特征' };
    }

    const restoredReferences = targetRecord.definition.references
      .map((reference): AxisSelectionCandidate | null => {
        const resolvedCandidate = this.resolveRestoredAxisSelectionCandidate(reference);
        if (!resolvedCandidate) {
          return null;
        }
        return {
          ...resolvedCandidate,
          label: resolvedCandidate.label || reference.entityId,
          payload: {
            ...(resolvedCandidate.payload ?? {}),
            assemblyTopologyRef: reference.assemblyTopologyRef ?? null,

              topologyPick: reference.topologyPick ?? readAxisSelectionTopologyPick(resolvedCandidate.payload),

          },
        };
      });
    if (restoredReferences.some((candidate) => candidate === null)) {
      return { ok: false, reason: '基准轴编辑特征参数恢复失败，存在失效引用' };
    }

    this.restoreEditingAxisVisibilityIfNeeded();
    this.pendingCreationContext = targetRecord.creationContext;
    this.editSession = {
      featureId: targetRecord.featureId,
      datumAxisId: axisId,
      axisName: targetAxis.name,
      previousVisible: targetAxis.isVisible(),
    };
    this.state = {
      lifecycle: ReferenceCommandLifecycle.Open,
      mode: targetRecord.definition.mode,
      modeSource: ReferenceCommandModeSource.ManualLocked,
      references: restoredReferences.filter((candidate): candidate is AxisSelectionCandidate => Boolean(candidate)),
      preview: { ok: false },
      dirty: false,
    };
    targetAxis.setVisible(false);
    this.recomputePreview();
    this.syncSelectionVisuals();
    this.viewer.setMode('reference-axis' as never);
    this.viewer.requestRender();
    return { ok: true };
  }


    /**
   * 取消命令。
   */
  public cancel(): void {

        const exitViewerMode = resolveReferenceCommandExitViewerMode(this.pendingCreationContext);

        this.restoreEditingAxisVisibilityIfNeeded();
    this.editSession = null;
    this.state = createDefaultState();
    this.pendingCreationContext = ReferenceCreationContext.PartFeature;
    this.clearPreview();
    this.options.onCancelled?.();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.setMode(exitViewerMode as never);
    this.viewer.requestRender();
  }

  /**
   * 处理新增选择。
   *
   * @param candidate 新选择候选。
   * @returns 是否接收本次选择。
   */
  public addSelection(candidate: AxisSelectionCandidate): boolean {
    if (this.state.lifecycle !== ReferenceCommandLifecycle.Open) {
      return false;
    }

    const transition = this.resolveAddSelectionTransition({
      currentMode: this.state.mode,
      modeSource: this.state.modeSource,
      references: this.state.references,
      candidate,
    });
    this.state.mode = transition.mode;
    this.state.references = transition.references;
    this.state.dirty = true;
    this.recomputePreview();
    this.viewer.requestRender();
    return true;
  }

  /**
   * 删除指定选择并重算模式与预览。
   *
   * @param entityId 要删除的实体 ID。
   */
  public removeSelection(entityId: string): void {
    const transition = this.resolveRemoveSelectionTransition({
      currentMode: this.state.mode,
      modeSource: this.state.modeSource,
      references: this.state.references,
      removedEntityId: entityId,
    });
    this.state.mode = transition.mode;
    this.state.references = transition.references;
    this.recomputePreview();
  }

  /**
   * 清空全部参考。
   */
  public clearSelections(): void {
    this.state.references = [];
    this.recomputePreview();
  }

  /**
   * 手动设置模式。
   *
   * @param mode 目标模式。
   */
  public setMode(mode: AxisMode): void {
    const transition = this.resolveManualModeTransition(mode, this.state.references);
    this.state.mode = transition.mode;
    this.state.modeSource = ReferenceCommandModeSource.ManualLocked;
    this.state.references = transition.references;
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 提交基准轴。
   * @returns 提交结果。
   */
  public submit(): { ok: boolean; featureId?: string; reason?: string } {
    if (!this.state.preview.ok) {
      return { ok: false, reason: this.state.preview.error?.message ?? '当前引用不足以定义唯一基准轴' };
    }

    const activeEditSession = this.editSession;
    const featureId = activeEditSession?.featureId ?? createAxisFeatureId();

      const exitViewerMode = resolveReferenceCommandExitViewerMode(this.pendingCreationContext);

      const definition = this.buildAxisDefinition(featureId, this.state);
    if (definition.mode === AxisMode.TwoPlanes && this.state.preview.geometry) {
      logReferenceAxisControllerDebug('创建正式基准轴前输出预览结果', {
        featureId,
        creationContext: this.pendingCreationContext,
        references: definition.references,
        origin: formatReferenceAxisDebugVector(this.state.preview.geometry.origin),
        direction: formatReferenceAxisDebugVector(this.state.preview.geometry.direction),
        start: formatReferenceAxisDebugVector(this.state.preview.geometry.displaySegment.start),
        end: formatReferenceAxisDebugVector(this.state.preview.geometry.displaySegment.end),
        displayLength: resolveReferenceAxisDebugLength(this.state.preview.geometry),
      });
    }
    const sourceLinearSegment = definition.mode === AxisMode.LineEdgeAxis
      ? tryExtractLinearSegmentFromSelection(this.state.references[0] ?? null)
      : null;
    if (activeEditSession) {
      const editableRecord = this.records.find((record) => record.featureId === featureId);
      if (!editableRecord) {
        return { ok: false, reason: '基准轴编辑提交失败，原特征记录不存在' };
      }
      editableRecord.creationContext = this.pendingCreationContext;
      editableRecord.definition = definition;
      editableRecord.resolvedGeometry = this.state.preview.geometry;
      editableRecord.sourceLinearSegment = sourceLinearSegment ?? undefined;
      editableRecord.hidden = false;
      editableRecord.suppressed = false;
      editableRecord.error = undefined;
      editableRecord.datumAxisId = activeEditSession.datumAxisId;

      const updated = this.updateDatumAxisFromPreview({
        axisId: activeEditSession.datumAxisId,
        axisName: activeEditSession.axisName,
        geometry: this.state.preview.geometry,
        visible: activeEditSession.previousVisible,
      });
      if (!updated) {
        return { ok: false, reason: '基准轴编辑提交失败，请检查目标基准轴是否仍存在' };
      }
    } else {
      this.records.push({
        featureId,
        creationContext: this.pendingCreationContext,
        definition,
        resolvedGeometry: this.state.preview.geometry,
        sourceLinearSegment: sourceLinearSegment ?? undefined,
        hidden: false,
      });
      this.createDatumAxisFromPreview(featureId, this.state.preview.geometry);
    }

    this.editSession = null;
    this.state = createDefaultState();
    this.pendingCreationContext = ReferenceCreationContext.PartFeature;
    this.clearPreview();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.setMode(exitViewerMode as never);
    this.viewer.requestRender();
    return { ok: true, featureId };
  }

  /**
   * 进入定义编辑态。
   *
   * @param featureId 特征 ID。
   * @returns 是否成功进入编辑态。
   */
  public editDefinition(featureId: string): boolean {
    const record = this.records.find((item) => item.featureId === featureId);
    if (!record) {
      return false;
    }
    this.pendingCreationContext = record.creationContext;

    this.state = {
      lifecycle: ReferenceCommandLifecycle.Open,
      mode: record.definition.mode,
      modeSource: ReferenceCommandModeSource.ManualLocked,
      references: record.definition.references.map((item) => ({
        entityId: item.entityId,
        entityType: item.entityType,
        label: item.entityId,
        kind: AxisSelectionKind.Unsupported,
        payload: {
          assemblyTopologyRef: item.assemblyTopologyRef ?? null,

            // 还原拓扑命中快照，供重算或二次编辑链路使用
          topologyPick: item.topologyPick ?? null,

        },
      })),
      preview: { ok: false },
      dirty: false,
    };

    this.recomputePreview();
    return true;
  }

  /**
   * 重建指定特征。
   *
   * @param featureId 特征 ID。
   * @returns 是否重建成功。
   */
  public rebuildFeature(featureId: string): boolean {
    const record = this.records.find((item) => item.featureId === featureId);
    if (!record) {
      return false;
    }

    if (record.definition.mode === AxisMode.TwoPlanes) {
      logReferenceAxisControllerDebug('开始重算双面基准轴', {
        featureId: record.featureId,
        creationContext: record.creationContext,
        references: record.definition.references,
        previousOrigin: formatReferenceAxisDebugVector(record.resolvedGeometry?.origin),
        previousDirection: formatReferenceAxisDebugVector(record.resolvedGeometry?.direction),
        previousStart: formatReferenceAxisDebugVector(record.resolvedGeometry?.displaySegment.start),
        previousEnd: formatReferenceAxisDebugVector(record.resolvedGeometry?.displaySegment.end),
        previousDisplayLength: resolveReferenceAxisDebugLength(record.resolvedGeometry),
      });
    }

    const tempState: AxisCommandState = {
      lifecycle: ReferenceCommandLifecycle.Open,
      mode: record.definition.mode,
      modeSource: ReferenceCommandModeSource.ManualLocked,
      references: record.definition.references.map((item) => ({
        entityId: item.entityId,
        entityType: item.entityType,
        label: item.entityId,
        kind: AxisSelectionKind.Unsupported,
        payload: {
          assemblyTopologyRef: item.assemblyTopologyRef ?? null,

            // 还原拓扑命中快照，供重算或二次编辑链路使用
          topologyPick: item.topologyPick ?? null,

        },
      })),
      preview: { ok: false },
      dirty: false,
    };

    const preview = this.solvePreview(tempState);
    if (!preview.ok) {
      if (record.definition.mode === AxisMode.TwoPlanes) {
        logReferenceAxisControllerDebug('双面基准轴重算失败', {
          featureId: record.featureId,
          error: preview.error ?? null,
        });
      }
      record.error = preview.error?.message ?? '重建失败';
      record.resolvedGeometry = undefined;
      return false;
    }

    if (record.definition.mode === AxisMode.TwoPlanes) {
      logReferenceAxisControllerDebug('双面基准轴重算结果', {
        featureId: record.featureId,
        origin: formatReferenceAxisDebugVector(preview.geometry?.origin),
        direction: formatReferenceAxisDebugVector(preview.geometry?.direction),
        start: formatReferenceAxisDebugVector(preview.geometry?.displaySegment.start),
        end: formatReferenceAxisDebugVector(preview.geometry?.displaySegment.end),
        displayLength: resolveReferenceAxisDebugLength(preview.geometry),
      });
    }

    record.error = undefined;
    record.resolvedGeometry = preview.geometry;
    this.rebuildDatumAxisRecord(record);
    return true;
  }


    /**
   * 重算装配体上下文创建的基准轴。
   *
   * 设计说明：
   * 1. 装配体零部件移动或旋转后，依赖特征线、点、面创建的基准轴必须重新求解；
   * 2. 基准轴已经保存 `AxisFeatureRecord.definition`，这里复用现有重建链路，避免引入第二套求解逻辑；
   * 3. 装配体拓扑稳定引用本身就是需要重算的依据，不能只依赖创建上下文标记；
   * 4. 默认基准轴、零件内基准轴和被压缩的特征不参与本次重算。
   *
   * @returns 成功重算的基准轴数量。
   */
  public recomputeAssemblyDatumAxes(): number {
    let recomputedCount = 0;

    this.records.forEach((record) => {
      const shouldRecompute = (
        record.creationContext === ReferenceCreationContext.Assembly
        || hasAssemblyTopologyAxisReference(record.definition)
      );
      if (!shouldRecompute || record.suppressed === true) {
        return;
      }
      if (this.rebuildFeature(record.featureId)) {
        recomputedCount += 1;
      }
    });

    if (recomputedCount > 0) {
      this.viewer.requestRender();
    }
    return recomputedCount;
  }


    /**
   * 捕获基准轴命令与正式对象历史快照。
   *
   * 设计说明：
   * 1. `DatumAxisManager` 只知道正式轴对象，不知道轴由哪些引用求解而来；
   * 2. 控制器持有 `AxisFeatureRecord`，这是装配体移动后重算的唯一可复算定义来源；
   * 3. 因此历史快照在控制器边界汇总 manager 快照与特征记录，保证 Undo/Redo 后对象状态与重算链路一致。
   *
   * @returns 基准轴历史快照。
   */
  public createHistorySnapshot(): DatumAxisHistorySnapshot {
    const managerSnapshot = this.viewer.datumAxisManager?.createHistorySnapshot() ?? {
      managerId: DATUM_AXIS_HISTORY_ENTITY_ID,
      axes: [],
      features: [],
    };

    return {
      ...managerSnapshot,
      features: this.records.map((record) => this.createHistoryFeatureRecord(record)),
    };
  }

  /**
   * 按历史快照恢复基准轴命令与正式对象状态。
   *
   * @param snapshot 基准轴历史快照。
   */
  public restoreHistorySnapshot(snapshot: DatumAxisHistorySnapshot): void {
    this.editSession = null;
    this.viewer.datumAxisManager?.restoreHistorySnapshot(snapshot);
    this.records = (snapshot.features ?? []).map((record) => this.createAxisFeatureRecordFromHistory(record));
    this.state = createDefaultState();
    this.pendingCreationContext = ReferenceCreationContext.PartFeature;
    this.clearPreview();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.requestRender();
  }

  /**
   * 从运行时特征记录创建历史记录。
   *
   * @param record 运行时特征记录。
   * @returns 可序列化历史记录。
   */
  private createHistoryFeatureRecord(record: AxisFeatureRecord): DatumAxisFeatureHistoryRecord {
    return {
      featureId: record.featureId,
      creationContext: record.creationContext,
      definition: this.cloneAxisFeatureDefinition(record.definition),
      resolvedGeometry: record.resolvedGeometry
        ? this.createHistoryResolvedGeometry(record.resolvedGeometry)
        : undefined,
      sourceLinearSegment: record.sourceLinearSegment
        ? {
          start: this.createHistoryVector(record.sourceLinearSegment.start),
          end: this.createHistoryVector(record.sourceLinearSegment.end),
        }
        : undefined,
      hidden: record.hidden,
      datumAxisId: record.datumAxisId,
      suppressed: record.suppressed,
      error: record.error,
    };
  }

  /**
   * 从历史记录恢复运行时特征记录。
   *
   * @param record 历史特征记录。
   * @returns 运行时特征记录。
   */
  private createAxisFeatureRecordFromHistory(record: DatumAxisFeatureHistoryRecord): AxisFeatureRecord {
    return {
      featureId: record.featureId,
      creationContext: record.creationContext,
      definition: this.cloneAxisFeatureDefinition(record.definition),
      resolvedGeometry: record.resolvedGeometry
        ? this.createResolvedGeometryFromHistory(record.resolvedGeometry)
        : undefined,
      sourceLinearSegment: record.sourceLinearSegment
        ? {
          start: this.createVectorFromHistory(record.sourceLinearSegment.start),
          end: this.createVectorFromHistory(record.sourceLinearSegment.end),
        }
        : undefined,
      hidden: record.hidden,
      datumAxisId: record.datumAxisId,
      suppressed: record.suppressed,
      error: record.error,
    };
  }

  /**
   * 克隆基准轴特征定义。
   *
   * @param definition 特征定义。
   * @returns 克隆后的特征定义。
   */
  private cloneAxisFeatureDefinition(definition: AxisFeatureDefinition): AxisFeatureDefinition {
    return JSON.parse(JSON.stringify(definition)) as AxisFeatureDefinition;
  }

  /**
   * 创建解析几何历史记录。
   *
   * @param geometry 运行时解析几何。
   * @returns 可序列化解析几何。
   */
  private createHistoryResolvedGeometry(geometry: AxisResolvedGeometry): DatumAxisResolvedGeometryHistoryRecord {
    return {
      origin: this.createHistoryVector(geometry.origin),
      direction: this.createHistoryVector(geometry.direction),
      startWorld: this.createHistoryVector(geometry.displaySegment.start),
      endWorld: this.createHistoryVector(geometry.displaySegment.end),
      validity: geometry.validity,
    };
  }

  /**
   * 从历史记录恢复解析几何。
   *
   * @param record 解析几何历史记录。
   * @returns 运行时解析几何。
   */
  private createResolvedGeometryFromHistory(record: DatumAxisResolvedGeometryHistoryRecord): AxisResolvedGeometry {
    return {
      origin: this.createVectorFromHistory(record.origin),
      direction: this.createVectorFromHistory(record.direction),
      displaySegment: {
        start: this.createVectorFromHistory(record.startWorld),
        end: this.createVectorFromHistory(record.endWorld),
      },
      validity: record.validity,
    };
  }

  /**
   * 创建历史向量。
   *
   * @param vector Three.js 向量。
   * @returns plain data 向量。
   */
  private createHistoryVector(vector: THREE.Vector3): DatumAxisHistoryVector3 {
    return [vector.x, vector.y, vector.z];
  }

  /**
   * 从历史向量创建 Three.js 向量。
   *
   * @param vector 历史向量。
   * @returns Three.js 向量。
   */
  private createVectorFromHistory(vector: DatumAxisHistoryVector3): THREE.Vector3 {
    return new THREE.Vector3(vector[0], vector[1], vector[2]);
  }


    /**
   * 重算零件特征上下文中依赖特征几何的基准轴。
   *
   * 设计说明：
   * 1. 特征 replay 会先清空并重建结果体，依赖特征面、边、点的基准轴必须在结果体同步完成后复算；
   * 2. 默认基准轴、无 topologyPick 的零件内基准轴和被压缩的特征不参与本次重算；
   * 3. 复用既有 rebuildFeature 链路，避免引入第二套求解逻辑。
   *
   * @returns 成功重算的基准轴数量。
   */
  public recomputePartFeatureDatumAxes(): number {
    let recomputedCount = 0;

    this.records.forEach((record) => {
      if (
        record.suppressed === true
        || record.creationContext !== ReferenceCreationContext.PartFeature
        || !this.hasFeatureGeometryAxisReference(record.definition)
      ) {
        return;
      }
      if (this.rebuildFeature(record.featureId)) {
        recomputedCount += 1;
      }
    });

    if (recomputedCount > 0) {
      this.viewer.requestRender();
    }
    return recomputedCount;
  }

  /**
   * 判断基准轴定义是否依赖特征结果体几何（即至少一条引用持有 topologyPick）。
   *
   * 设计说明：
   * 仅依赖基准几何（DatumPlane/DatumAxis/DatumPoint）的基准轴不需要随特征结果体重算；
   * 依赖特征面、线、点的基准轴会在创建时写入 topologyPick，以此作为判断依据。
   *
   * @param definition 基准轴特征定义。
   * @returns 若任意引用持有 topologyPick 则返回 `true`。
   */
  private hasFeatureGeometryAxisReference(definition: AxisFeatureDefinition): boolean {
    if (!definition || !Array.isArray(definition.references)) {
      return false;
    }
    return definition.references.some((reference) => Boolean(reference.topologyPick));
  }


    /**
   * 同步基准轴当前选择的可视状态。
   */
  public syncSelectionVisuals(): void {
    const selectedObjects = this.state.references
      .map((reference) => this.resolveSelectionObject(reference.entityId))
      .filter((object): object is THREE.Object3D => object !== null);
    this.viewer.entitySelectionManager?.updateSelectionState(
      selectedObjects.map((object) => ({
        distance: 0,
        object,
        point: object.getWorldPosition(new THREE.Vector3()),
      })),
    );
    this.viewer.requestRender();
  }

  /**
   * 将场景对象解析为基准轴选择候选。
   *
   * @param object 场景对象。
   * @returns 选择候选；不支持时返回 `null`。
   */
  public resolveSelectionCandidateByObject(object: AxisSelectableObject): AxisSelectionCandidate | null {
    if (isDatumAxis(object)) {
      const endpoints = object.getWorldEndpoints();
      const datumAxisId = object.datumAxisId;
      return {
        entityId: buildDatumReferenceEntityId(DatumReferenceEntityKind.Axis, datumAxisId),
        entityType: ReferenceEntitySemanticType.DatumAxis,
        label: object.name || buildDatumReferenceEntityId(DatumReferenceEntityKind.Axis, datumAxisId),
        kind: AxisSelectionKind.ReferenceAxis,
        payload: endpoints
          ? {
            startPoint: endpoints.start,
            endPoint: endpoints.end,
            origin: object.getWorldMidpoint(),
            direction: object.getWorldDirection(),
          }
          : undefined,
      };
    }

    const datumPlaneId = getDatumPlaneIdFromObject(object);
    if (datumPlaneId !== null) {
      const datumPlane = this.viewer.getDatumPlane(datumPlaneId);
      if (!datumPlane) {
        return null;
      }
      return {
        entityId: buildDatumReferenceEntityId(DatumReferenceEntityKind.Plane, datumPlane.id),
        entityType: ReferenceEntitySemanticType.DatumPlane,
        label: datumPlane.name || buildDatumReferenceEntityId(DatumReferenceEntityKind.Plane, datumPlane.id),
        kind: AxisSelectionKind.PlaneLike,
        payload: {
          planeNormal: datumPlane.normal.clone(),
          planePoint: datumPlane.originWorld.clone(),
          origin: datumPlane.originWorld.clone(),
        },
      };
    }

    if (isDatumPoint(object)) {
      const datumPointId = object.datumPointId;
      return {
        entityId: buildDatumReferenceEntityId(DatumReferenceEntityKind.Point, datumPointId),
        entityType: ReferenceEntitySemanticType.DatumPoint,
        label: object.name || buildDatumReferenceEntityId(DatumReferenceEntityKind.Point, datumPointId),
        kind: AxisSelectionKind.PointLike,
        payload: {
          point: object.getWorldPoint(),
        },
      };
    }

    if (isFeatureLine(object)) {
      const endpoints = object.getWorldEndpoints();
      return {
        entityId: object.uuid,
        entityType: ReferenceEntitySemanticType.FeatureLine,
        label: object.name || object.uuid,
        kind: AxisSelectionKind.Linear,
        payload: endpoints
          ? {
            ...buildAxisSelectionTopologyPayload(object),
            startPoint: endpoints.start,
            endPoint: endpoints.end,
          }
          : buildAxisSelectionTopologyPayload(object),
      };
    }

    if (isFeatureFace(object)) {
      const analyticSurface = resolveAxisSurfaceCandidate(object);
      if (analyticSurface) {
        return {
          entityId: object.uuid,
          entityType: analyticSurface.entityType,
          label: object.name || object.uuid,
          kind: AxisSelectionKind.CylOrConeFace,
          payload: {
            ...buildAxisSelectionTopologyPayload(object),
            surfaceType: mapAxisSurfaceTypeToTopologySurfaceKind(analyticSurface.surfaceType),
            axisOrigin: analyticSurface.axisOrigin,
            axisDirection: analyticSurface.axisDirection,
            axisDisplayLength: analyticSurface.axisDisplayLength,
            planePoint: analyticSurface.surfacePoint,
            faceOuterLoopVertices: analyticSurface.faceOuterLoopVertices,
          },
        };
      }


        if (isFeatureSurface(object)) {
        const axis = resolveCylOrConeAxisFromFeatureFace(object);
        const surfacePoint = object.getWorldPoint();
        const surfacePlane = object.getWorldPlane();
        const axisOrigin = axis?.origin ?? surfacePoint;
        const axisDirection = axis?.direction ?? surfacePlane.normal;
        return {
          entityId: object.uuid,
          entityType: ReferenceEntitySemanticType.FeatureSurface,
          label: object.name || object.uuid,
          kind: AxisSelectionKind.CylOrConeFace,
          payload: {
            ...buildAxisSelectionTopologyPayload(object),
            surfaceKind: object.surfaceKind,
            surfaceType: object.surfaceKind,
            axisOrigin,
            axisDirection,
            axisDisplayLength: axis?.displayLength,
            planePoint: surfacePoint,
            faceOuterLoopVertices: object.getWorldBoundaryVertices(),
          },
        };
      }


        const plane = object.getWorldPlane();
      return {
        entityId: object.uuid,
        entityType: ReferenceEntitySemanticType.FeatureFace,
        label: object.name || object.uuid,
        kind: AxisSelectionKind.PlaneLike,
        payload: {
          ...buildAxisSelectionTopologyPayload(object),
          planeNormal: plane.normal,
          planePoint: plane.point,
          faceOuterLoopVertices: object.getWorldBoundaryVertices(),
        },
      };
    }

    if (isFeaturePoint(object)) {
      return {
        entityId: object.uuid,
        entityType: ReferenceEntitySemanticType.FeaturePoint,
        label: object.name || object.uuid,
        kind: AxisSelectionKind.PointLike,
        payload: {
          ...buildAxisSelectionTopologyPayload(object),
          point: object.getWorldPoint(),
        },
      };
    }

    return null;
  }

  /**
   * 按对象 ID 解析基准轴选择候选。
   *
   * @param objectId 场景对象 ID。
   * @returns 选择候选；不支持时返回 `null`。
   */
  public resolveSelectionCandidateByObjectId(objectId: string): AxisSelectionCandidate | null {
    const datumPlaneId = parseDatumReferenceEntityId(objectId, DatumReferenceEntityKind.Plane);
    if (datumPlaneId !== null) {
      const datumPlane = this.viewer.getDatumPlane(datumPlaneId);
      return datumPlane ? this.resolveSelectionCandidateByObject(datumPlane.getObject3D()) : null;
    }

    const datumAxisId = parseDatumReferenceEntityId(objectId, DatumReferenceEntityKind.Axis);
    if (datumAxisId !== null) {
      const datumAxis = this.viewer.getDatumAxis(datumAxisId);
      return datumAxis ? this.resolveSelectionCandidateByObject(datumAxis) : null;
    }

    const datumPointId = parseDatumReferenceEntityId(objectId, DatumReferenceEntityKind.Point);
    if (datumPointId !== null) {
      const datumPoint = this.viewer.getDatumPoint(datumPointId);
      return datumPoint ? this.resolveSelectionCandidateByObject(datumPoint) : null;
    }

    const object = this.viewer.getSceneObjectById(objectId);
    if (!object) {
      return null;
    }
    return this.resolveSelectionCandidateByObject(object);
  }

    /**
   * 直接消费一个场景对象选择。
   *
   * @param object 场景对象。
   * @returns 是否接收本次选择。
   */
  public handleSelectionObject(object: AxisSelectableObject): boolean {
    const candidate = this.resolveSelectionCandidateByObject(object);
    if (!candidate) {
      return false;
    }
    const accepted = this.addSelection(candidate);
    if (accepted) {
      this.syncSelectionVisuals();
    }
    return accepted;
  }

  /**
   * 解析基准轴引用实体。
   * @param reference 引用实体。
   * @returns 已解析引用；不存在时返回 `null`。
   */
  public resolveReference(reference: AxisEntityReference): AxisResolvedEntity | null {
    const entityId = reference.entityId;
    const datumPlaneId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Plane);
    if (datumPlaneId !== null) {
      const plane = this.viewer.getDatumPlane(datumPlaneId);
      if (!plane) {
        return null;
      }
      return {
        id: entityId,
        type: ReferenceEntitySemanticType.DatumPlane,
        data: {
          planeNormal: plane.normal.clone(),
          planePoint: plane.originWorld.clone(),
          origin: plane.originWorld.clone(),
        },
      };
    }

    const datumAxisId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Axis);
    if (datumAxisId !== null) {
      const datumAxis = this.viewer.getDatumAxis(datumAxisId);
      if (!datumAxis) {
        return null;
      }
      const endpoints = datumAxis.getWorldEndpoints();
      if (!endpoints) {
        return null;
      }
      return {
        id: entityId,
        type: ReferenceEntitySemanticType.DatumAxis,
        data: {
          startPoint: endpoints.start,
          endPoint: endpoints.end,
          origin: datumAxis.getWorldMidpoint() ?? endpoints.start.clone(),
          direction: datumAxis.getWorldDirection(),
        },
      };
    }

    const datumPointId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Point);
    if (datumPointId !== null) {
      const datumPoint = this.viewer.getDatumPoint(datumPointId);
      if (!datumPoint) {
        return null;
      }
      return {
        id: entityId,
        type: ReferenceEntitySemanticType.DatumPoint,
        data: {
          point: datumPoint.getWorldPoint(),
        },
      };
    }


      const object = this.resolveTopologyPriorityAxisReferenceObject(reference)
      ?? this.viewer.getSceneObjectById(entityId);


      if (!object) {
      // uuid 查找失败时，尝试通过 topologyPick 从当前结果体上重新解析代理对象。
      // 特征 replay 后旧代理 uuid 已失效，但拓扑索引仍然稳定，可用于重建引用。
      return this.resolveAxisReferenceFromTopologyPick(reference);
    }


      if (isFeatureLine(object)) {
      const endpoints = object.getWorldEndpoints();
      const direction = object.getWorldDirection();
      if (!endpoints || !direction) {
        return null;
      }
      return {
        id: entityId,
        type: ReferenceEntitySemanticType.FeatureLine,
        data: {
          startPoint: endpoints.start,
          endPoint: endpoints.end,
          origin: endpoints.start.clone(),
          direction,
        },
      };
    }

    if (isFeaturePoint(object)) {
      return {
        id: entityId,
        type: ReferenceEntitySemanticType.FeaturePoint,
        data: {
          point: object.getWorldPoint(),
        },
      };
    }

    if (isFeatureFace(object)) {
      const analyticSurface = resolveAxisSurfaceCandidate(object);
      if (analyticSurface) {
        const analyticBounds = resolveReferenceAxisWorldBounds(object);
        return {
          id: entityId,
          type: ReferenceEntitySemanticType.FeatureSurface,
          data: {
            surfaceType: mapAxisSurfaceTypeToTopologySurfaceKind(analyticSurface.surfaceType),
            axisOrigin: analyticSurface.axisOrigin,
            axisDirection: analyticSurface.axisDirection,
            axisDisplayLength: analyticSurface.axisDisplayLength,
            planePoint: analyticSurface.surfacePoint,
            origin: analyticSurface.surfacePoint,
            faceOuterLoopVertices: analyticSurface.faceOuterLoopVertices,
            bboxMin: analyticBounds?.min,
            bboxMax: analyticBounds?.max,
          },
        };
      }


        if (isFeatureSurface(object)) {
        const axis = resolveCylOrConeAxisFromFeatureFace(object);
        const surfacePoint = object.getWorldPoint();
        const surfacePlane = object.getWorldPlane();
        const axisOrigin = axis?.origin ?? surfacePoint;
        const axisDirection = axis?.direction ?? surfacePlane.normal;
        const surfaceBounds = resolveReferenceAxisWorldBounds(object);
        return {
          id: entityId,
          type: ReferenceEntitySemanticType.FeatureSurface,
          data: {
            surfaceType: object.surfaceKind,
            axisOrigin,
            axisDirection,
            axisDisplayLength: axis?.displayLength,
            origin: axisOrigin,
            direction: axisDirection,
            planePoint: surfacePoint,
            faceOuterLoopVertices: object.getWorldBoundaryVertices(),
            bboxMin: surfaceBounds?.min,
            bboxMax: surfaceBounds?.max,
          },
        };
      }


        const plane = object.getWorldPlane();
      const boundaryVertices = object.getWorldBoundaryVertices();
      const faceBounds = resolveReferenceAxisWorldBounds(object);
      logReferenceAxisControllerDebug('双面基准轴面引用解析', {
        entityId,
        boundaryVertexCount: boundaryVertices.length,
        bboxMin: formatReferenceAxisDebugVector(faceBounds?.min ?? null),
        bboxMax: formatReferenceAxisDebugVector(faceBounds?.max ?? null),
      });
      return {
        id: entityId,
        type: ReferenceEntitySemanticType.FeatureFace,
        data: {
          planeNormal: plane.normal,
          planePoint: plane.point,
          faceOuterLoopVertices: boundaryVertices,
          bboxMin: faceBounds?.min,
          bboxMax: faceBounds?.max,
        },
      };
    }

    return null;
  }

  /**
   * 基于预览结果创建正式基准轴。
   *
   * @param featureId 特征 ID。
   * @param geometry 预览几何。
   */
  private createDatumAxisFromPreview(featureId: string, geometry: AxisResolvedGeometry | undefined): void {
    if (!geometry) {
      return;
    }
    const record = this.records.find((item) => item.featureId === featureId);
    const axis = this.viewer.datumAxisManager.createAxis({
      creationContext: record?.creationContext ?? this.pendingCreationContext,
      name: '',
      originWorld: geometry.origin.clone(),
      direction: geometry.direction.clone(),
      startWorld: geometry.displaySegment.start.clone(),
      endWorld: geometry.displaySegment.end.clone(),
      visible: true,
    });
    if (!axis) {
      return;
    }
    if (record?.definition.mode === AxisMode.TwoPlanes) {
      logReferenceAxisControllerDebug('已创建双面基准轴正式对象', {
        featureId,
        datumAxisId: axis.datumAxisId,
        origin: formatReferenceAxisDebugVector(geometry.origin),
        direction: formatReferenceAxisDebugVector(geometry.direction),
        start: formatReferenceAxisDebugVector(geometry.displaySegment.start),
        end: formatReferenceAxisDebugVector(geometry.displaySegment.end),
        displayLength: resolveReferenceAxisDebugLength(geometry),
      });
    }
    if (record) {
      record.datumAxisId = axis.datumAxisId;
    }
  }

    /**
   * 恢复基准轴引用对应的当前选择候选。
   *
   * 设计说明：
   * 1. 编辑特征恢复时也必须遵循拓扑优先，避免 PropertyManager 展示旧代理参数；
   * 2. 求解入口已通过 `resolveReference()` 拓扑优先，这里只负责 UI 候选恢复一致性；
   * 3. 拓扑无法恢复时才回退到旧实体 ID，兼容历史记录。
   *
   * @param reference 基准轴实体引用。
   * @returns 当前可用选择候选；无法恢复时返回 `null`。
   */
  private resolveRestoredAxisSelectionCandidate(reference: AxisEntityReference): AxisSelectionCandidate | null {
    const topologyObject = this.resolveTopologyPriorityAxisReferenceObject(reference);
    if (topologyObject) {
      const topologyCandidate = this.resolveSelectionCandidateByObject(topologyObject);
      if (topologyCandidate) {
        return topologyCandidate;
      }
    }

    return this.resolveSelectionCandidateByObjectId(reference.entityId);
  }

  /**
   * 若当前存在编辑会话，恢复被临时隐藏的正式基准轴可见性。
   */
  private restoreEditingAxisVisibilityIfNeeded(): void {
    const activeEditSession = this.editSession;
    if (!activeEditSession) {
      return;
    }
    const targetAxis = this.viewer.getDatumAxis(activeEditSession.datumAxisId);
    targetAxis?.setVisible(activeEditSession.previousVisible);
  }

    /**
   * 用预览结果更新已有正式基准轴。
   *
   * @param input 更新输入。
   * @returns 更新成功返回 `true`。
   */
  private updateDatumAxisFromPreview(input: {
    axisId: number;
    axisName: string;
    geometry: AxisResolvedGeometry | undefined;
    visible: boolean;
  }): boolean {
    if (!input.geometry) {
      return false;
    }

    const existingAxis = this.viewer.getDatumAxis(input.axisId);
    if (existingAxis) {
      existingAxis.updateFrame({
        originWorld: input.geometry.origin,
        direction: input.geometry.direction,
        startWorld: input.geometry.displaySegment.start,
        endWorld: input.geometry.displaySegment.end,
      });
      existingAxis.setVisible(input.visible);
      return true;
    }

    this.viewer.datumAxisManager.createAxis({
      id: input.axisId,
      creationContext: this.pendingCreationContext,
      name: input.axisName,
      originWorld: input.geometry.origin.clone(),
      direction: input.geometry.direction.clone(),
      startWorld: input.geometry.displaySegment.start.clone(),
      endWorld: input.geometry.displaySegment.end.clone(),
      visible: input.visible,
    });
    return true;
  }

  /**
   * 获取指定模式对应的选择规则。
   *
   * @param mode 目标模式。
   * @returns 选择规则。
   */
  private getSelectionRule(mode: AxisMode): AxisSelectionRule {
    return ReferenceAxisController.AXIS_SELECTION_RULES.find((item) => item.mode === mode)
      ?? ReferenceAxisController.AXIS_SELECTION_RULES[0];
  }

  /**
   * 推断最可能的建轴模式。
   *
   * @param candidates 当前候选集合。
   * @returns 推断模式。
   */
  private inferAxisMode(candidates: AxisSelectionCandidate[]): AxisMode {
    const usable = candidates.filter((item) => item.kind !== AxisSelectionKind.Unsupported);
    const points = usable.filter((item) => item.kind === AxisSelectionKind.PointLike);
    const planes = usable.filter((item) => item.kind === AxisSelectionKind.PlaneLike);
    const cylCones = usable.filter((item) => item.kind === AxisSelectionKind.CylOrConeFace);
    const linears = usable.filter((item) => (
      item.kind === AxisSelectionKind.Linear
      || item.kind === AxisSelectionKind.ReferenceAxis
      || item.kind === AxisSelectionKind.TemporaryAxis
    ));

    if (points.length >= 2) {
      return AxisMode.TwoPoints;
    }
    if (planes.length >= 2) {
      return AxisMode.TwoPlanes;
    }
    if (planes.length === 1 && points.length === 0 && cylCones.length === 0 && linears.length === 0) {
      return AxisMode.TwoPlanes;
    }
    if (points.length >= 1 && (planes.length + cylCones.length) >= 1) {
      return AxisMode.PointAndFaceOrPlane;
    }
    if (cylCones.length >= 1) {
      return AxisMode.CylOrConeFace;
    }
    if (linears.length >= 1) {
      return AxisMode.LineEdgeAxis;
    }
    return AxisMode.LineEdgeAxis;
  }

  /**
   * 解析命令打开时的模式与引用。
   *
   * @param preselection 预选实体列表。
   * @returns 打开迁移结果。
   */
  private resolveOpenTransition(preselection: AxisSelectionCandidate[]): AxisOpenTransitionResult {
    const inferredMode = this.inferAxisMode(preselection);
    const normalizedReferences = this.normalizeReferencesForMode(inferredMode, preselection);
    return {
      mode: inferredMode,
      references: normalizedReferences,
    };
  }

  /**
   * 解析新增一个选择后的迁移结果。
   *
   * @param params 新增选择上下文。
   * @returns 选择迁移结果。
   */
  private resolveAddSelectionTransition(params: {
    currentMode: AxisMode;
    modeSource: ReferenceCommandModeSource;
    references: AxisSelectionCandidate[];
    candidate: AxisSelectionCandidate;
  }): AxisSelectionTransitionResult {
    const { currentMode, modeSource, references, candidate } = params;
    const hasExistingCandidate = references.some((item) => item.entityId === candidate.entityId);

    if (hasExistingCandidate) {
      if (currentMode === AxisMode.LineEdgeAxis) {
        return {
          mode: currentMode,
          references: [candidate],
        };
      }

      const remainingReferences = references.filter((item) => item.entityId !== candidate.entityId);
      const nextMode = modeSource === ReferenceCommandModeSource.ManualLocked ? currentMode : this.inferAxisMode(remainingReferences);
      return {
        mode: nextMode,
        references: this.normalizeReferencesForMode(nextMode, remainingReferences),
      };
    }

    if (currentMode === AxisMode.LineEdgeAxis && modeSource !== ReferenceCommandModeSource.ManualLocked) {
      const mergedReferences = [...references, candidate];
      const inferredMode = this.inferAxisMode(mergedReferences);
      if (inferredMode !== AxisMode.LineEdgeAxis) {
        return {
          mode: inferredMode,
          references: this.normalizeReferencesForMode(inferredMode, mergedReferences),
        };
      }
    }

    if (currentMode === AxisMode.LineEdgeAxis) {
      return {
        mode: currentMode,
        references: [candidate],
      };
    }

    const mergedReferences = [...references, candidate];
    const nextMode = modeSource === ReferenceCommandModeSource.ManualLocked ? currentMode : this.inferAxisMode(mergedReferences);
    return {
      mode: nextMode,
      references: this.normalizeReferencesForMode(nextMode, mergedReferences),
    };
  }

  /**
   * 解析删除一个选择后的迁移结果。
   *
   * @param params 删除选择上下文。
   * @returns 选择迁移结果。
   */
  private resolveRemoveSelectionTransition(params: {
    currentMode: AxisMode;
    modeSource: ReferenceCommandModeSource;
    references: AxisSelectionCandidate[];
    removedEntityId: string;
  }): AxisSelectionTransitionResult {
    const { currentMode, modeSource, references, removedEntityId } = params;
    const remainingReferences = references.filter((item) => item.entityId !== removedEntityId);
    const nextMode = modeSource === ReferenceCommandModeSource.ManualLocked ? currentMode : this.inferAxisMode(remainingReferences);
    return {
      mode: nextMode,
      references: this.normalizeReferencesForMode(nextMode, remainingReferences),
    };
  }

  /**
   * 解析手动切换模式后的迁移结果。
   *
   * @param mode 目标模式。
   * @param references 当前参考。
   * @returns 选择迁移结果。
   */
  private resolveManualModeTransition(
    mode: AxisMode,
    references: AxisSelectionCandidate[],
  ): AxisSelectionTransitionResult {
    return {
      mode,
      references: this.normalizeReferencesForMode(mode, references),
    };
  }

  /**
   * 构建基准轴定义。
   *
   * @param featureId 特征 ID。
   * @param state 命令状态。
   * @returns 特征定义。
   */
  private buildAxisDefinition(featureId: string, state: AxisCommandState): AxisFeatureDefinition {
    return {
      id: featureId,
      type: 'AxisFeature',
      mode: state.mode,
      references: this.buildAxisReferences(state.references),
      version: 1,
    };
  }

    /**
   * 重建正式基准轴可视对象。
   *
   * @param record 特征记录。
   */
  private rebuildDatumAxisRecord(record: AxisFeatureRecord): void {
    if (!record.resolvedGeometry) {
      return;
    }
    if (typeof record.datumAxisId === 'number') {
      const existingAxis = this.viewer.getDatumAxis(record.datumAxisId);
      if (existingAxis) {
        existingAxis.updateFrame({
          originWorld: record.resolvedGeometry.origin,
          direction: record.resolvedGeometry.direction,
          startWorld: record.resolvedGeometry.displaySegment.start,
          endWorld: record.resolvedGeometry.displaySegment.end,
        });
        if (record.definition.mode === AxisMode.TwoPlanes) {
          logReferenceAxisControllerDebug('已写回双面基准轴正式对象', {
            featureId: record.featureId,
            datumAxisId: existingAxis.datumAxisId,
            origin: formatReferenceAxisDebugVector(record.resolvedGeometry.origin),
            direction: formatReferenceAxisDebugVector(record.resolvedGeometry.direction),
            start: formatReferenceAxisDebugVector(record.resolvedGeometry.displaySegment.start),
            end: formatReferenceAxisDebugVector(record.resolvedGeometry.displaySegment.end),
            displayLength: resolveReferenceAxisDebugLength(record.resolvedGeometry),
          });
        }
        existingAxis.setVisible(!record.hidden);
        return;
      }
      record.datumAxisId = undefined;
    }

    const axis = this.viewer.datumAxisManager.createAxis({
      creationContext: record.creationContext,
      name: '',
      originWorld: record.resolvedGeometry.origin.clone(),
      direction: record.resolvedGeometry.direction.clone(),
      startWorld: record.resolvedGeometry.displaySegment.start.clone(),
      endWorld: record.resolvedGeometry.displaySegment.end.clone(),
      visible: !record.hidden,
    });
    if (axis) {
      if (record.definition.mode === AxisMode.TwoPlanes) {
        logReferenceAxisControllerDebug('重算后补建双面基准轴正式对象', {
          featureId: record.featureId,
          datumAxisId: axis.datumAxisId,
          origin: formatReferenceAxisDebugVector(record.resolvedGeometry.origin),
          direction: formatReferenceAxisDebugVector(record.resolvedGeometry.direction),
          start: formatReferenceAxisDebugVector(record.resolvedGeometry.displaySegment.start),
          end: formatReferenceAxisDebugVector(record.resolvedGeometry.displaySegment.end),
          displayLength: resolveReferenceAxisDebugLength(record.resolvedGeometry),
        });
      }
      record.datumAxisId = axis.datumAxisId;
    }
  }

  /**
   * 按当前模式归一化参考集合。
   *
   * @param mode 当前模式。
   * @param references 待归一化参考。
   * @returns 归一化后的参考集合。
   */
  private normalizeReferencesForMode(mode: AxisMode, references: AxisSelectionCandidate[]): AxisSelectionCandidate[] {
    const rule = this.getSelectionRule(mode);
    const legal = this.dedupeReferencesKeepLast(references.filter((item) => rule.accept(item)));

    if (mode === AxisMode.PointAndFaceOrPlane) {
      const point = [...legal].reverse().find((item) => item.kind === AxisSelectionKind.PointLike);
      const face = [...legal].reverse().find((item) => (
        item.kind === AxisSelectionKind.PlaneLike || item.kind === AxisSelectionKind.CylOrConeFace
      ));
      const pair = [point, face].filter(Boolean) as AxisSelectionCandidate[];
      return pair.slice(0, rule.maxCount);
    }

    return legal.slice(-rule.maxCount);
  }

  /**
   * 按最后一次出现保留唯一参考。
   *
   * @param references 原始参考集合。
   * @returns 去重后的参考集合。
   */
  private dedupeReferencesKeepLast(references: AxisSelectionCandidate[]): AxisSelectionCandidate[] {
    const seen = new Set<string>();
    const reversed: AxisSelectionCandidate[] = [];
    for (let index = references.length - 1; index >= 0; index -= 1) {
      const item = references[index];
      if (seen.has(item.entityId)) {
        continue;
      }
      seen.add(item.entityId);
      reversed.push(item);
    }
    return reversed.reverse();
  }

  /**
   * 求解当前预览。
   *
   * @param state 命令状态。
   * @returns 预览状态。
   */
  private solvePreview(state: AxisCommandState): AxisPreviewState {
    const definition = this.buildAxisDefinition('__axis_preview__', state);
    const result = this.solver.solve(definition);
    if ('error' in result) {
      return { ok: false, error: result.error };
    }
    return { ok: true, geometry: result.geometry };
  }

  /**
   * 重算预览。
   */
  private recomputePreview(): void {
    if (this.state.lifecycle !== ReferenceCommandLifecycle.Open) {
      this.state.preview = { ok: false };
      this.clearPreview();
      this.viewer.requestRender();
      return;
    }

    this.state.preview = this.solvePreview(this.state);
    this.handlePreviewChanged(this.state.preview);
    this.viewer.requestRender();
  }

  /**
   * 处理预览变化。
   *
   * @param preview 预览状态。
   */
  private handlePreviewChanged(preview: AxisPreviewState): void {
    if (!preview.ok || !preview.geometry) {
      this.clearPreview();
      return;
    }
    this.renderPreview(preview.geometry);
  }

  /**
   * 渲染基准轴预览。
   *
   * @param geometry 预览几何。
   */
  private renderPreview(geometry: AxisResolvedGeometry): void {
    this.clearPreview();
    const lineGeometry = new THREE.BufferGeometry().setFromPoints([
      geometry.displaySegment.start.clone(),
      geometry.displaySegment.end.clone(),
    ]);
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x2aa6ff,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
      depthWrite: false,
    });
    const line = new THREE.Line(lineGeometry, lineMaterial);
    line.name = '__reference_axis_preview__';
    line.renderOrder = 992;
    this.previewObject = line;
        this.viewer.addObject(line);
  }

  /**
   * 清理预览。
   */
  private clearPreview(): void {
    if (!this.previewObject) {
      return;
    }
    this.viewer.scene.remove(this.previewObject);
    this.previewObject.traverse((object) => {
      const line = object as THREE.Line;
      if (line.geometry instanceof THREE.BufferGeometry) {
        line.geometry.dispose();
      }
      const material = (line as unknown as { material?: THREE.Material | THREE.Material[] }).material;
      if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
      } else if (material instanceof THREE.Material) {
        material.dispose();
      }
    });
    this.previewObject = null;
  }

  /**
   * 获取基准轴求解容差。
   *
   * @returns 当前容差策略。
   */
  public getAxisTolerance(): AxisTolerancePolicy {
    return this.tolerance;
  }

  /**
   * 构建几何引用列表。
   *
   * @param candidates 候选集合。
   * @returns 几何引用列表。
   */
  private buildAxisReferences(candidates: AxisSelectionCandidate[]): AxisEntityReference[] {
    return candidates.map((item, index) => ({
      entityId: item.entityId,
      entityType: item.entityType,
      slot: `ref-${index + 1}`,
      assemblyTopologyRef: readAxisSelectionAssemblyTopologyRef(item.payload),

        // 将拓扑命中快照写入定义，确保特征重建后重算链路能按索引解析新代理对象。
      topologyPick: readAxisSelectionTopologyPick(item.payload),

    }));
  }

  /**
   * 获取参考几何共享包围盒。
   *
   * @returns 模型包围盒；为空时返回 `null`。
   */
  public getModelBoundingBox(): { min: THREE.Vector3; max: THREE.Vector3 } | null {
    const sceneBounds = new THREE.Box3().setFromObject(this.viewer.scene);
    if (sceneBounds.isEmpty()) {
      return null;
    }
    return {
      min: sceneBounds.min.clone(),
      max: sceneBounds.max.clone(),
    };
  }

  /**
   * 解析基准轴选择对应的场景对象。
   *
   * @param entityId 引用实体 ID。
   * @returns 场景对象；无法映射时返回 `null`。
   */
  private resolveSelectionObject(entityId: string): THREE.Object3D | null {
    const datumPlaneId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Plane);
    if (datumPlaneId !== null) {
      return this.viewer.getDatumPlane(datumPlaneId)?.getObject3D() ?? null;
    }
    const datumAxisId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Axis);
    if (datumAxisId !== null) {
      return this.viewer.getDatumAxis(datumAxisId);
    }
    const datumPointId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Point);
    if (datumPointId !== null) {
      return this.viewer.getDatumPoint(datumPointId);
    }
    return this.viewer.getSceneObjectById(entityId);
  }

    /**
   * 按拓扑引用优先解析基准轴参考对象。
   *
   * 设计说明：
   * 1. 基准轴创建和重算时，导入拓扑与特征拓扑引用比旧代理 uuid 更权威；
   * 2. 旧代理仍在场景内时也必须先尝试拓扑恢复，避免使用过期端点、法向或面边界；
   * 3. 该函数只服务基准轴控制器，不改变通用选择对象解析函数。
   *
   * @param reference 基准轴实体引用。
   * @returns 拓扑恢复出的场景对象；无法恢复时返回 `null`。
   */
  private resolveTopologyPriorityAxisReferenceObject(reference: AxisEntityReference): THREE.Object3D | null {
    if (reference.assemblyTopologyRef) {
      const assemblyTopologyObject = resolveAssemblyTopologyReferenceObject(this.viewer, reference.assemblyTopologyRef);
      if (assemblyTopologyObject) {
        return assemblyTopologyObject;
      }
    }

    if (!reference.topologyPick) {
      return null;
    }

    return resolveModelTopologyProxyObject({
      scene: this.viewer.scene,
      topologyPick: reference.topologyPick,
      getSceneObjectById: this.viewer.getSceneObjectById.bind(this.viewer),
    });
  }


    /**
   * 根据拓扑命中快照解析当前结果体上的特征引用。
   *
   * 设计说明：
   * 1. 特征 replay 后旧代理对象 uuid 可能失效，但 `topologyPick` 保存了结果体 ID 与拓扑索引；
   * 2. 基准轴重算必须解析当前结果体上的代理对象，不能继续持有旧对象；
   * 3. 与 ReferencePlaneController.resolveFeatureReferenceFromTopologyPick 完全对称，
   *    但返回类型是 `AxisResolvedEntity`，通过 `resolveSelectionCandidateByObject` 中介转换。
   *
   * @param reference 基准轴实体引用。
   * @returns 当前结果体上解析得到的已解析实体；无法解析时返回 `null`。
   */
  private resolveAxisReferenceFromTopologyPick(reference: AxisEntityReference): AxisResolvedEntity | null {
    const topologyPick = reference.topologyPick;
    if (!topologyPick) {
      return null;
    }

    // 按拓扑类型与索引从新结果体上获取代理对象，再走既有 resolveReference 解析路径。
    const proxyObject = resolveModelTopologyProxyObject({
      scene: this.viewer.scene,
      topologyPick,
      getSceneObjectById: this.viewer.getSceneObjectById.bind(this.viewer),
    });
    if (!proxyObject) {
      return null;
    }

    // 复用既有代理对象解析路径，避免重复维护几何数据提取逻辑。
    const resolvedCandidate = this.resolveSelectionCandidateByObject(proxyObject as never);
    if (!resolvedCandidate) {
      return null;
    }

    // 将选择候选转换为求解器所需的已解析实体格式。
    return this.resolveReference({
      ...reference,
      entityId: proxyObject.uuid,
      topologyPick: null, // 避免递归：已经从新代理获取，不再走 topologyPick 路径
    });
  }


}
